import * as Highcharts from "highcharts";
import * as React from "react";
import HighchartsReact from "highcharts-react-official";
import HighchartsStock from "highcharts/highstock";
import colors from "../../../../styles/colors";
import { IBrushChartData, createGradient, timeTakenFormatter } from "../../../../formatters/analytics";
import {
  defaultStyle,
  defaultAxisLabels,
  defaultTooltipSize,
  defaultSmallAxisLabels,
  handleStepAreaChart,
} from "../../../../utils/charts";
import { UserFilter } from "../../../../App/Analytics/Common";

type FilterFn = (minTime: number, maxTime: number) => void;
type RemoveFn = (name: string, type: UserFilter) => () => void;

interface IBrushChartProps {
  data: IBrushChartData[];
  domain: [number, number];
  isFilterActive: boolean;
  maxRange: number;
  onRemoveFilter: RemoveFn;
  updateChartFilter: FilterFn;
}

interface Datum {
  learners: number[];
  time: number[];
  filteredLearners?: number[];
}

const DEFAULT_STEP = 12;
const SELECTION_ID = "selection";

export const getTimeTakenData = (data: IBrushChartData[]): Datum => {
  const result = { learners: [], time: [] } as Datum;
  data.forEach(curr => {
    result.learners.push(curr.students);
    result.time.push(curr.time);
    if (curr.filtered !== undefined) {
      result.filteredLearners = result.filteredLearners || [];
      result.filteredLearners.push(curr.filtered);
    }
  });
  return result;
};

const filterNonEqualLearners = (learners: number[], filteredLearners: number[]) => {
  const result = new Array(learners.length);

  for (let i = 0; i < learners.length; i++) {
    learners[i] !== filteredLearners[i] ? (result[i] = learners[i]) : (result[i] = 0);
  }

  return result;
};

const options = (
  data: IBrushChartData[],
  updateChartFilter: FilterFn,
  onRemoveFilter: RemoveFn,
  isFilterActive: boolean
): Highcharts.Options => {
  const datum = getTimeTakenData(data);
  return {
    chart: {
      events: {
        click() {
          this.xAxis[0].removePlotLine(SELECTION_ID);
          onRemoveFilter("course", UserFilter.TimeTakenBetween)();
        },
        /* tslint:disable-next-line */
        selection: function(event) {
          this.xAxis[0].removePlotLine(SELECTION_ID);

          const min = Math.ceil(event.xAxis[0].min);
          const max = Math.floor(event.xAxis[0].max);
          updateChartFilter(datum.time[min], datum.time[max]);

          this.xAxis[0].addPlotBand({
            color: colors.chartFilteredGray30,
            from: event.xAxis[0].min,
            id: SELECTION_ID,
            to: event.xAxis[0].max,
          });
          return false;
        },
        redraw() {
          // Filter
          if (!isFilterActive) {
            this.xAxis[0].removePlotLine(SELECTION_ID);
          }

          const newChart = handleStepAreaChart(this, DEFAULT_STEP);
          if (newChart !== undefined) {
            this.update({
              xAxis: newChart,
            });
          }
        },
      },
      type: "areaspline",
      zooming: {
        key: "shift",
        resetButton: {
          position: {
            verticalAlign: "bottom",
          },
          theme: {
            style: {
              fontSize: "1.4rem",
            },
          },
        },
        type: "x",
      },
    },
    plotOptions: {
      areaspline: {
        lineWidth: 1,
        linecap: "round",
      },
      series: {
        dataLabels: {
          style: {
            fontSize: "1.4rem",
          },
        },
        stacking: "normal",
      },
    },
    series: [
      !isFilterActive &&
        ({
          data: datum.learners,
          name: "Learners",
          /* tslint:disable-next-line */
          color: createGradient(colors.chartLightBlue60, colors.chartLightBlue10),
          lineColor: colors.chartLightBlue100,
        } as Highcharts.SeriesAreaOptions),

      isFilterActive &&
        datum.filteredLearners &&
        ({
          data: datum.filteredLearners,
          name: "Filtered",
          /* tslint:disable-next-line */
          color: createGradient(colors.chartLightBlue60, colors.chartLightBlue10),
          lineColor: colors.chartLightBlue100,
        } as Highcharts.SeriesAreaOptions),

      isFilterActive &&
        datum.filteredLearners &&
        ({
          data: filterNonEqualLearners(datum.learners, datum.filteredLearners),
          name: "Learners",
          /* tslint:disable-next-line */
          color: createGradient(colors.chartFilteredGray100, colors.chartFilteredGray0),
          lineColor: colors.chartFilteredGray100,
        } as Highcharts.SeriesAreaOptions),
    ].filter(Boolean) as Highcharts.SeriesOptionsType[],
    title: {
      text: undefined,
    },
    tooltip: {
      ...defaultTooltipSize,
    },
    xAxis: {
      ...defaultSmallAxisLabels(DEFAULT_STEP),
      categories: datum.time.map(timeTakenFormatter),
      lineColor: colors.chartLightBlue100,
      minPadding: 0,
    },
    yAxis: {
      max: Math.max(...datum.learners),
      title: {
        text: undefined,
      },
      ...defaultAxisLabels(),
    },
    ...defaultStyle,
  };
};

const BrushChart: React.FC<IBrushChartProps> = ({ data, updateChartFilter, onRemoveFilter, isFilterActive }) => (
  <HighchartsReact
    containerProps={{ style: { width: "100%", height: "100%", display: "block" } }}
    highcharts={HighchartsStock}
    options={options(data, updateChartFilter, onRemoveFilter, isFilterActive)}
  />
);

export default BrushChart;
